import { useEffect, useMemo, useState } from 'react'
import { Search as SearchIcon } from 'lucide-react'
import { BoxTruckDriverLastLocation, BoxTruckNearbyDriversSort, CanonicalLoad, formatBoxTruckDockHeight } from '@numeo/types-core'
import { LoadBid } from '@numeo/types'
import { Spinner } from '@/components/ds2'
import { SortableTableHeader } from '@/components/shared/SortableTableHeader'
import { useDriverGroupBadges } from '../hooks/useDriverGroupBadges'
import { useNearbyDriversPage } from '../hooks/useNearbyDriversPage'
import { BoxTruckVerification, buildBoxTruckLoadAppLinkUrl, fetchBoxTruckRecruitSmsTemplate, NearbyBoxTruckDriver } from '../services/boxTruckLoadsApi'
import { BOX_TRUCK_RECRUIT_SMS, buildDriverSmsMessage } from '../utils/driverSmsTemplate'
import { DriverRoadDistanceState } from '../types/roadDistance'
import { NearbyDriverRouteModal } from './NearbyDriverRouteModal'
import { BoxTruckFilterSelect } from './BoxTruckFilterSelect'
import { NearbyDriverDistanceInfo } from './NearbyDriverDistanceCell'
import { NearbyDriverRow } from './NearbyDriverRow'
import { NEARBY_DEFAULT_RADIUS_MI, NEARBY_MOBILE_RADIUS_OPTIONS, NEARBY_RADIUS_OPTIONS } from '../constants/nearbyDrivers'

interface NearbyDriversTabProps {
    /** The load whose origin drives the search + the SMS nudge + the route map. Optional: when absent, the
     *  Search Vehicles drawer drives the search off `origin` instead, texts the recruiting template, and the
     *  route-to-load action is hidden (there's no load to route to). */
    load?: CanonicalLoad
    /** Manual search origin — used only when `load` is absent (the Search Vehicles drawer). */
    origin?: { lat: number; lng: number } | null
    /** Header title. Defaults to "Nearby Drivers". */
    title?: string
    /** Externally-controlled radius (miles). When set, the built-in radius dropdown is hidden — the parent
     *  (the Search Vehicles drawer) owns the radius via its own form. */
    radiusMi?: number
    /** Driver bids on THIS load. A nearby driver who has bid shows their bid (price + when) on their card,
     *  so the operator sees the offer and the driver's live location together. Keyed by bidderId ↔ driverId. */
    bids?: LoadBid[]
    /** The driver currently picked (checkbox) to offer as the unit — its info fills the Offer email. */
    selectedDriverId?: string | null
    /** Pick a driver (or `null` to clear). Present → each row shows a checkbox. */
    onSelectDriver?: (driver: NearbyBoxTruckDriver | null) => void
    /** On-demand GraphHopper distance state retained per driver for this open drawer. */
    roadDistanceStates?: Record<string, DriverRoadDistanceState>
    /** Invalidate/recalculate routed mileage after an operator changes this driver's location. */
    onDriverLocationChanged?: (driver: NearbyBoxTruckDriver) => void
    /** The stored fix itself, for the host that owns `pinnedDrivers`. Distinct from the callback above: that
     *  one carries a merged ROW and exists to invalidate routed mileage, while the pinned snapshot needs the
     *  fix exactly as the server returned it — `source` and `fixAt` included, neither of which a row carries. */
    onDriverLocationSaved?: (userId: string, lastLocation: BoxTruckDriverLastLocation) => void
    /** Report the complete fetched driver list to an optional parent consumer: bidders, then picked drivers, then
     *  nearest first — by distance whatever the table is sorted by, because the host's first row means "nearest".
     *  `nearestKnown` is false when that cannot be promised: under latest-location order the server's nearest read
     *  failed, and the first row is only the nearest of the newest locations, so it must not stand in for it. */
    onDriversChange?: (drivers: NearbyBoxTruckDriver[], nearestKnown: boolean) => void
    /**
     * Open this driver's SMS conversation. Omitted by hosts that have nowhere to show one, and the
     * column then does not exist at all.
     *
     * Separate from `onSelectDriver` on purpose: the checkbox picks whose unit the OFFER EMAIL quotes,
     * and texting someone is not that decision. Clicking SMS on a row must not rewrite the email the
     * dispatcher is composing for somebody else.
     */
    onOpenSms?: (driver: NearbyBoxTruckDriver) => void
    /** The driver whose conversation is open, so its row shows which one the panel is on. */
    smsDriverId?: string | null
    /** Extra controls rendered at the right end of the header row (after the radius select) — the drawer
     *  slots its Close ✕ here now that the drivers panel spans the drawer's full top edge. */
    headerExtra?: React.ReactNode
    /** The board tab's picked "Search for driver" drivers: shown at the TOP of the list with a "Selected"
     *  chip — the operator opened this load FOR them. Subject to the radius like everyone else: a pick the
     *  search can't place inside it does not appear (see the fetch effect). On an ORIGIN-LESS load there is
     *  no radius to apply, so a row is synthesized from the verification object and they stay visible. */
    pinnedDrivers?: BoxTruckVerification[]
}

/** Stable empty default for the optional pinned-drivers prop — a fresh `[]` per render would churn the
 *  memos the fetch effect keys off. */
const EMPTY_PINNED: BoxTruckVerification[] = []

/** Nearby Drivers — caller-owned Box Truck drivers (pending and verified) who shared a location within an
 *  adjustable radius of this load's origin. Bidders lead, then picked drivers, then everyone else; every
 *  group follows the operator's sort — latest location update first by default, or nearest first. Which
 *  drivers, in what order, is `useNearbyDriversPage`; this draws them. */
export const NearbyDriversTab = ({
    load,
    origin: originProp,
    title,
    radiusMi: radiusMiProp,
    bids,
    selectedDriverId,
    onSelectDriver,
    roadDistanceStates = {},
    onDriverLocationChanged,
    onDriverLocationSaved,
    onDriversChange,
    onOpenSms,
    smsDriverId,
    headerExtra,
    pinnedDrivers = EMPTY_PINNED,
}: NearbyDriversTabProps) => {
    // The TEAM's saved recruiting template (null until loaded → the bundled default). Fetched only in the
    // recruiting (no-load) context. Without this every operator sent the bundled default — which carries ANOTHER
    // carrier's address + MC/DOT — even when they had customized their own template on My Drivers.
    const [recruitTemplate, setRecruitTemplate] = useState<string | null>(null)
    useEffect(() => {
        if (load) return // load context nudges about the load, not the recruiting template
        void fetchBoxTruckRecruitSmsTemplate()
            .then(setRecruitTemplate)
            .catch(() => undefined)
    }, [load])
    // SMS body for a driver: the load nudge (+ app link) when opened from a load, else the team's recruiting
    // pitch the My Drivers page uses (with the same `Hi {first}, ` greeting) — there's no load to link to in the
    // standalone Search Vehicles context.
    const smsText = (name: string | null) =>
        load
            ? `Hi ${name?.split(' ')[0] || 'driver'}, ${buildDriverSmsMessage(load, buildBoxTruckLoadAppLinkUrl(load))}`
            : `Hi ${name?.split(' ')[0] || 'driver'}, ${recruitTemplate ?? BOX_TRUCK_RECRUIT_SMS}`
    const [radiusMiState, setRadiusMi] = useState(NEARBY_DEFAULT_RADIUS_MI)
    // Externally-controlled radius (the drawer's form) wins over the built-in dropdown when provided.
    const radiusMi = radiusMiProp ?? radiusMiState
    const {
        list: { rows, pinnedIdSet, updateDriver },
        sorting: { sort, changeSort, reordering, reorderError },
        status: { loading, error },
        paging: { hasMore, loadingMore, loadMoreError, loadMore },
        origin,
    } = useNearbyDriversPage({ load, origin: originProp, radiusMi, bids, pinnedDrivers, onDriversChange })
    // Bid lookup by bidder — a nearby driver's card shows their bid on this load when they've placed one.
    const bidByBidder = useMemo(() => new Map((bids ?? []).map((b) => [b.bidderId, b])), [bids])
    // The group badge of each listed driver — its own request, for every role. A pinned row this tab builds
    // itself is asked about too, so its badge is as fresh as the rest rather than read from the saved pick.
    const groupBadges = useDriverGroupBadges(useMemo(() => rows.map((driver) => driver.claimDriverId), [rows]))
    // Quick client-side filter over the fetched list (name / phone / city / equipment / truck#) — the list is
    // small (one radius fetch), so no server round-trip.
    const [search, setSearch] = useState('')
    // The driver whose route map (driver → pickup → delivery) is open, if any.
    const [routeDriver, setRouteDriver] = useState<NearbyBoxTruckDriver | null>(null)

    // Rows actually shown — the search narrows the display only; onDriversChange still reports the full list.
    const visible = useMemo(() => {
        const q = search.trim().toLowerCase()
        if (!q) return rows
        return rows.filter((d) =>
            [
                d.name,
                d.phone,
                d.city,
                d.state,
                d.postalCode,
                d.equipment,
                d.truckNumber,
                formatBoxTruckDockHeight(d.dockHeightRamps),
                ...(d.equipmentCapabilities ?? []),
            ].some((v) => v && v.toLowerCase().includes(q))
        )
    }, [rows, search])

    // Origin-less load AND no pinned drivers → nothing to show. With pinned drivers the normal layout
    // renders below (the fetch effect seeded their synthesized rows), just without a meaningful radius.
    if (!origin.searchable && pinnedIdSet.size === 0) {
        return (
            <div>
                {/* Header still renders — headerExtra carries the drawer's Close ✕, which must stay reachable
                    even when the load has no usable origin coordinates. */}
                <div className="mb-2 flex items-center justify-between gap-3">
                    <h3 className="text-sm font-semibold text-foreground">{title ?? 'Nearby Drivers'}</h3>
                    {headerExtra}
                </div>
                <div className="rounded-lg border border-border px-3 py-4 text-xs text-foreground-muted">
                    {load ? "Load origin coordinates unavailable — can't match nearby drivers." : 'Pick a location to find nearby drivers.'}
                </div>
            </div>
        )
    }

    return (
        <div className="flex min-h-0 flex-1 flex-col">
            <div className="mb-2 flex flex-col items-stretch justify-between gap-2 min-[900px]:flex-row min-[900px]:items-center min-[900px]:gap-3">
                <div className="flex min-w-0 flex-col items-stretch gap-2 min-[900px]:flex-row min-[900px]:items-center min-[900px]:gap-3">
                    <h3 className="shrink-0 text-sm font-semibold text-foreground">
                        {title ?? 'Nearby Drivers'}
                        {loading ? '' : ` (${rows.length}${hasMore ? '+' : ''})`}
                        {reordering && (
                            <span className="ml-2 inline-flex items-center gap-1 text-xs font-normal text-foreground-muted">
                                <Spinner size={10} /> Updating…
                            </span>
                        )}
                    </h3>
                    <div className="relative">
                        <SearchIcon size={13} className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-foreground-muted/70" />
                        <input
                            type="text"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            placeholder="Search drivers…"
                            aria-label="Search nearby drivers"
                            className="h-7 w-full rounded-lg border border-border bg-background pl-7 pr-2 text-xs text-foreground outline-none placeholder:text-foreground-muted/60 focus:border-accent min-[900px]:w-56"
                        />
                    </div>
                </div>
                <div className="flex items-center justify-end gap-2">
                    {/* Built-in radius dropdown — hidden when the parent controls the radius via its own form. */}
                    {radiusMiProp == null && (
                        <>
                            <div className="w-28 min-[900px]:hidden">
                                <BoxTruckFilterSelect
                                    value={String(radiusMi)}
                                    onChange={(value) => setRadiusMi(Number(value))}
                                    options={NEARBY_MOBILE_RADIUS_OPTIONS}
                                    className="w-full"
                                />
                            </div>
                            <select
                                value={radiusMi}
                                onChange={(e) => setRadiusMi(Number(e.target.value))}
                                className="hidden h-7 cursor-pointer rounded-lg bg-default px-2 text-xs font-medium text-foreground outline-none focus-visible:ring-2 focus-visible:ring-accent min-[900px]:block"
                                aria-label="Search radius in miles"
                            >
                                {NEARBY_RADIUS_OPTIONS.map((r) => (
                                    <option key={r} value={r}>
                                        {r} mi
                                    </option>
                                ))}
                            </select>
                        </>
                    )}
                    {headerExtra}
                </div>
            </div>

            {loading ? (
                <div className="rounded-lg border border-border px-3 py-4 text-xs text-foreground-muted">Loading nearby drivers…</div>
            ) : error ? (
                <div className="rounded-lg border border-border px-3 py-4 text-xs text-danger">Couldn&apos;t load nearby drivers. Try again.</div>
            ) : rows.length === 0 ? (
                <div className="rounded-lg border border-border px-3 py-4 text-xs text-foreground-muted">
                    No drivers within {radiusMi} mi have shared a location.
                </div>
            ) : visible.length === 0 ? (
                <div className="rounded-lg border border-border px-3 py-4 text-xs text-foreground-muted">No drivers match “{search.trim()}”.</div>
            ) : (
                <>
                    {reorderError && (
                        <div className="mb-2 rounded-lg border border-border px-3 py-2 text-xs text-danger">
                            Couldn&apos;t load the drivers in this order. Showing the ones already loaded.
                        </div>
                    )}
                    {/* The table scrolls BOTH ways in here, not inside the host's vertical scroll: there, the
                        horizontal scrollbar sat under the last row, below the fold on any real list, and Note and the
                        row actions on a narrow screen were reachable only with Shift+wheel. A host that gives this
                        component a bounded flex column pins the bar to the bottom of the visible pane, and the
                        Dispatch Board's always-drawn scrollbar keeps it visible on macOS too. */}
                    <div className="dispatch-board-scrollbar -mx-3 min-h-0 overflow-auto overscroll-contain border-y border-border">
                        {/* ONE element per column, ONE line per row — atrek-dense. Everything that used to stack
                        inside a cell (phone under name, time under location, payload under dims, note under
                        status) is its own column now, so the row height collapses to a single line. */}
                        <table className="w-full border-collapse text-xs">
                            <thead>
                                <tr className="border-b border-border bg-background text-left text-[10px] font-semibold uppercase tracking-wider text-foreground-muted">
                                    {onSelectDriver && <th className="w-8 px-2 py-1.5" />}
                                    <SortableTableHeader
                                        label="Miles out"
                                        trailing={<NearbyDriverDistanceInfo />}
                                        columnKey={BoxTruckNearbyDriversSort.DISTANCE}
                                        sortColumn={sort}
                                        sortDirection="asc"
                                        onSortChange={changeSort}
                                        uiVariant="v2"
                                        emphasizeActive
                                        className="w-px whitespace-nowrap px-2 py-1.5"
                                    />
                                    {onOpenSms && <th className="w-px whitespace-nowrap px-2 py-1.5">SMS</th>}
                                    <th className="px-2 py-1.5">Driver</th>
                                    <th className="w-px whitespace-nowrap px-2 py-1.5">Vehicle</th>
                                    {/* Newest first: the time the location was last updated, not the place name. */}
                                    <SortableTableHeader
                                        label="Location"
                                        columnKey={BoxTruckNearbyDriversSort.RECENT}
                                        sortColumn={sort}
                                        sortDirection="desc"
                                        onSortChange={changeSort}
                                        uiVariant="v2"
                                        emphasizeActive
                                        className="px-2 py-1.5"
                                    />
                                    <th className="px-2 py-1.5">Dims</th>
                                    <th className="px-2 py-1.5">Status</th>
                                    <th className="px-2 py-1.5">Note</th>
                                    <th className="px-2 py-1.5" />
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-border">
                                {visible.map((d) => (
                                    <NearbyDriverRow
                                        key={d.driverId}
                                        driver={d}
                                        bid={bidByBidder.get(d.driverId)}
                                        selected={selectedDriverId === d.driverId}
                                        pinned={pinnedIdSet.has(d.driverId)}
                                        teamGroup={d.claimDriverId ? groupBadges.get(d.claimDriverId) : undefined}
                                        roadDistanceState={roadDistanceStates[d.driverId]}
                                        smsOpen={smsDriverId === d.driverId}
                                        smsText={smsText(d.name)}
                                        origin={origin.effective}
                                        onSelectDriver={onSelectDriver}
                                        onOpenSms={onOpenSms}
                                        onOpenRoute={load ? setRouteDriver : undefined}
                                        onPatch={(patch) => updateDriver(d.driverId, patch)}
                                        onDriverLocationSaved={onDriverLocationSaved}
                                        onDriverLocationChanged={onDriverLocationChanged}
                                    />
                                ))}
                            </tbody>
                            {(hasMore || loadMoreError) && (
                                <tfoot>
                                    <tr>
                                        <td colSpan={onSelectDriver ? 9 : 8} className="px-3 py-3 text-center">
                                            <div className="flex flex-col items-center gap-2">
                                                {loadMoreError && <span className="text-xs text-danger">Couldn&apos;t load the next drivers. Try again.</span>}
                                                {hasMore && (
                                                    <button
                                                        type="button"
                                                        onClick={() => void loadMore()}
                                                        disabled={loadingMore}
                                                        className="inline-flex h-8 items-center justify-center rounded-3xl bg-default px-4 text-xs font-medium text-default-foreground transition-colors duration-150 hover:bg-default-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2 focus-visible:ring-offset-surface disabled:cursor-not-allowed disabled:opacity-50"
                                                    >
                                                        {loadingMore ? 'Loading…' : 'Load more'}
                                                    </button>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                </tfoot>
                            )}
                        </table>
                    </div>
                </>
            )}

            {load && routeDriver && <NearbyDriverRouteModal driver={routeDriver} load={load} onClose={() => setRouteDriver(null)} />}
        </div>
    )
}
